import {useEffect, memo, Profiler} from 'react';

import {DOC_TYPE} from '@/constants/document/doc';
import * as store from '@/store/index';
import sdk from '@/sdk';
import {ErrorCode} from '@/sdk/common/error';
import {setDocumentTitle} from '@/utils/browserUtils';
import {getUrlParams, removeSearchParamNoRefresh} from '@/utils/urlUtils';

import {perfLogger, PROFILER_KEY} from '@/buried/performance';
import {importAsyncComponent} from '@/utils/loadable';
import {isAdminRepository} from '@/store/util';
import {Role} from '@/Constant';
import {clickOpenMoveTo, feLogger} from '@/buried';
import TocError, {ErrorType} from '@/tocPages/components/Error';

import Document from '@/domains/document/DocHome/Document';
const Book = importAsyncComponent(() => import('./Book'));

interface IDocHomeProps {
    repository: Repository;
    docGuid: string;
    curDocument?: WizDocument;
    curDocumentError?: any | null;
    setHeaderSlot?: (node: any) => void;
}

function DocHome({
    repository,
    docGuid,
    curDocument,
    curDocumentError,
    setHeaderSlot,
}: IDocHomeProps) {
    const isBook = curDocument?.type === DOC_TYPE.DIRECTORY;
    const disabledMoveTo = !(isAdminRepository() || curDocument?.docRoleName === Role.DocAdmin);
    const {moveTo} = getUrlParams()!;

    useEffect(() => {
        // 链接引导默认打开移动到页面
        if (moveTo === '1' && !disabledMoveTo && !curDocumentError) {
            store.setMoveDocument(docGuid);
            removeSearchParamNoRefresh('moveTo');
            feLogger(clickOpenMoveTo({
                userId: sdk.as.user?.userGuid || '',
                docGuid: docGuid || '',
            }));
        }
    }, [curDocumentError, disabledMoveTo, docGuid, moveTo]);

    if (docGuid === curDocument?.docGuid) {
        setDocumentTitle(curDocument.name);
    }

    function getCodeType(code: string | number) {
        if (code === ErrorCode.NotFound) {
            return ErrorType.DocNotFound;
        } else if (code === ErrorCode.NoPermission) {
            return ErrorType.DocNoPermission;
        }
        return ErrorType.DocRetry;
    }

    // 权限申请页
    if (curDocumentError) {
        return <TocError type={getCodeType(curDocumentError?.code)} />;
    }

    if (!curDocument) {
        return null;
    }

    if (isBook) {
        return <Book
            curDocument={curDocument}
            repository={repository}
            docGuid={docGuid}
            setHeaderSlot={setHeaderSlot}
        />;
    }

    return (
        <>
            <Profiler
                id={PROFILER_KEY.document}
                onRender={perfLogger.compRender.onRender}
            >
                <Document
                    curDocument={curDocument}
                    curDocumentError={curDocumentError}
                    repository={repository}
                    docGuid={docGuid}
                    setHeaderSlot={setHeaderSlot}
                />
            </Profiler>
        </>
    );
}

export default store.connect([
    store.KEYS.CUR_DOCUMENT,
    store.KEYS.CUR_DOCUMENT_ERROR,
])(memo(DocHome));
